'use client'

import classNames from 'clsx'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import RequestedClubIcon from '@/assets/icons/leaderboard/lb-requested-club.svg'
import { URLS } from '@/configs/urls'
import S from '@/features/leaderboard/components/LeaderboardRow.module.scss'
import {
  CountryFlag,
  FanAvatar,
  RankCell,
  RowGlow,
  rowTone,
  SportIcon,
  toneRowClass,
  useCountry
} from '@/features/leaderboard/components/RowParts'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import { Link } from '@/i18n/routing'
import Image from '@/ui/MediaImage'
import { shouldUnoptimizeImageSrc } from '@/utils/imageUrl'

import type { FansLeaderboardClub, FansLeaderboardRow } from '../types'

const FALLBACK_CREST = '/assets/clubs/fans-leaderboard-placeholder.webp'

/** `task`: one task's board — the fan and the views only, no details. */
export type FanRowVariant = 'global' | 'club' | 'task'

type FanRowProps = {
  row: FansLeaderboardRow
  variant: FanRowVariant
  isSelf: boolean
  highlighted?: boolean
}

function ClubLogo({ club, size }: { club: FansLeaderboardClub; size: number }) {
  const src = club.logoUrl || FALLBACK_CREST
  return (
    <Image
      src={src}
      alt=""
      width={size}
      height={size}
      className={S.clubLogo}
      unoptimized={shouldUnoptimizeImageSrc(src)}
    />
  )
}

function RequestedClub({ row }: { row: FansLeaderboardRow }) {
  const t = useTranslations('leaderboardBoard')
  const sport = row.requestedClubSport
  return (
    <>
      <span className={S.requestedLogo} aria-hidden>
        <RequestedClubIcon />
      </span>
      <span className={S.clubName}>
        {t(row.requestedAthlete ? 'requestedAthlete' : 'requestedClub')}
      </span>
      {sport ? (
        <div className={S.clubMeta}>
          <SportIcon src={sport.iconUrl} />
          <span className={S.clubSport}>{sport.name}</span>
        </div>
      ) : null}
    </>
  )
}

function FavoriteClubCell({ row }: { row: FansLeaderboardRow }) {
  const t = useTranslations('leaderboardBoard')
  const { club } = row
  const country = useCountry(club?.country)

  if (club) {
    return (
      <div className={classNames(S.colClub, S.desktopOnly)}>
        <ClubLogo club={club} size={30} />
        <Link
          href={URLS.CLUB_DETAIL(club.id)}
          className={classNames(S.clubName, S.nameLink)}
        >
          {club.name}
        </Link>
        <div className={S.clubMeta}>
          <CountryFlag code={country.code} />
          {country.code && club.sportName ? <span>·</span> : null}
          <SportIcon src={club.sportIconUrl} />
          {club.sportName ? (
            <span className={S.clubSport}>{club.sportName}</span>
          ) : null}
          {club.fanCount !== undefined ? (
            <span className={S.clubFans}>
              ·{' '}
              {t('clubFans', {
                count: club.fanCount,
                fans: formatLeaderboardNumber(club.fanCount)
              })}
            </span>
          ) : null}
        </div>
      </div>
    )
  }

  if (row.hasRequestedClub) {
    return (
      <div className={classNames(S.colClub, S.desktopOnly)}>
        <RequestedClub row={row} />
      </div>
    )
  }

  return <div className={classNames(S.colClub, S.desktopOnly)} />
}

function DetailsClub({ row }: { row: FansLeaderboardRow }) {
  const t = useTranslations('leaderboardBoard')
  const { club } = row
  const country = useCountry(club?.country)

  if (!club && !row.hasRequestedClub) {
    return null
  }

  return (
    <div className={S.detailsClub}>
      {club ? (
        <>
          <ClubLogo club={club} size={24} />
          <Link
            href={URLS.CLUB_DETAIL(club.id)}
            className={classNames(S.clubName, S.nameLink)}
          >
            {club.name}
          </Link>
          <div className={S.clubMeta}>
            <CountryFlag code={country.code} />
            {country.name ? <span>{country.name}</span> : null}
            {country.name && club.sportName ? <span>·</span> : null}
            <SportIcon src={club.sportIconUrl} />
            {club.sportName ? (
              <span className={S.clubSport}>{club.sportName}</span>
            ) : null}
          </div>
          {club.fanCount !== undefined ? (
            <div className={S.detailsClubFans}>
              <span className={S.detailsClubFansValue}>
                {formatLeaderboardNumber(club.fanCount)}
              </span>
              <span className={S.detailsClubFansLabel}>
                {t('fansUnit', { count: club.fanCount })}
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <RequestedClub row={row} />
      )}
    </div>
  )
}

/** One fan on the global or club board (Figma "LB Row / Fan"). */
export function FanRow({
  row,
  variant,
  isSelf,
  highlighted = false
}: FanRowProps) {
  const t = useTranslations('leaderboardBoard')
  const [expanded, setExpanded] = useState(false)
  const country = useCountry(row.country)
  const isTask = variant === 'task'
  const tone = rowTone(row.rank, isSelf)
  const viewsClass = classNames(
    S.value,
    S.valueMain,
    tone === 'self' && S.valueSelf
  )

  return (
    <div
      className={classNames(
        S.row,
        toneRowClass[tone],
        highlighted && S.highlight
      )}
    >
      <RowGlow tone={tone} />
      <div className={S.summary}>
        {isTask ? null : (
          <button
            type="button"
            className={S.expandHit}
            aria-expanded={expanded}
            onClick={() => setExpanded(value => !value)}
          >
            <span className={S.srOnly}>{t('toggleDetails')}</span>
          </button>
        )}

        <RankCell rank={row.rank} tone={tone} />

        <div className={S.colMain}>
          <FanAvatar src={row.avatarUrl} alt={row.name} />
          <div className={S.identity}>
            <div className={S.nameLine}>
              <span className={S.name}>{row.name}</span>
              {isSelf ? <span className={S.tag}>{t('you')}</span> : null}
            </div>
            {country.name || row.rankName ? (
              <div className={S.meta}>
                <CountryFlag code={country.code} />
                {country.name ? <span>{country.name}</span> : null}
                {country.name && row.rankName ? <span>·</span> : null}
                {row.rankName ? <span>{row.rankName}</span> : null}
              </div>
            ) : null}
          </div>
        </div>

        {variant === 'global' ? <FavoriteClubCell row={row} /> : null}

        <div className={classNames(S.colViews, S.desktopOnly)}>
          <span className={viewsClass}>
            {formatLeaderboardNumber(row.totalViews)}
          </span>
        </div>
        {isTask ? null : (
          <>
            <div className={classNames(S.colStat, S.colBest, S.desktopOnly)}>
              <span className={S.value}>
                {formatLeaderboardNumber(row.bestViews)}
              </span>
            </div>
            <div className={classNames(S.colStat, S.desktopOnly)}>
              <span className={S.value}>
                {formatLeaderboardNumber(row.arenaPoints)}
              </span>
            </div>
            <div className={classNames(S.colTasks, S.desktopOnly)}>
              <span className={S.value}>
                {formatLeaderboardNumber(row.tasks)}
              </span>
            </div>
          </>
        )}

        <div className={classNames(S.mobileViews, S.mobileOnly)}>
          <span className={viewsClass}>
            {formatLeaderboardNumber(row.totalViews)}
          </span>
          <span className={S.mobileViewsLabel}>{t('columns.views')}</span>
        </div>
      </div>

      {expanded ? (
        <div className={S.details}>
          {variant === 'global' ? <DetailsClub row={row} /> : null}
          <div className={S.detailsStats}>
            <div className={S.detailsStat}>
              <span className={S.detailsStatLabel}>
                {t('columns.bestViews')}
              </span>
              <span className={S.detailsStatValue}>
                {formatLeaderboardNumber(row.bestViews)}
              </span>
            </div>
            <div className={S.detailsStat}>
              <span className={S.detailsStatLabel}>
                {t('columns.arenaPoints')}
              </span>
              <span className={S.detailsStatValue}>
                {formatLeaderboardNumber(row.arenaPoints)}
              </span>
            </div>
            <div className={S.detailsStat}>
              <span className={S.detailsStatLabel}>{t('columns.tasks')}</span>
              <span className={S.detailsStatValue}>
                {formatLeaderboardNumber(row.tasks)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** Column titles for the fan rows; cells reuse the row column classes. */
export function FanRowHeader({ variant }: { variant: FanRowVariant }) {
  const t = useTranslations('leaderboardBoard')
  return (
    <>
      <div className={classNames(S.colRank, S.headerCell)}>
        {t('columns.position')}
      </div>
      <div className={classNames(S.colMain, S.headerCell)}>
        {t('columns.fan')}
      </div>
      {variant === 'global' ? (
        <div className={classNames(S.colClub, S.headerCell)}>
          {t('columns.favorite')}
        </div>
      ) : null}
      <div className={classNames(S.colViews, S.headerCell, S.headerMain)}>
        {t('columns.views')}
      </div>
      {variant === 'task' ? null : (
        <>
          <div className={classNames(S.colStat, S.colBest, S.headerCell)}>
            {t('columns.bestViews')}
          </div>
          <div className={classNames(S.colStat, S.headerCell)}>
            {t('columns.arenaPoints')}
          </div>
          <div className={classNames(S.colTasks, S.headerCell)}>
            {t('columns.tasks')}
          </div>
        </>
      )}
    </>
  )
}
